// @ts-ignore
import React, { useEffect } from "react";

import { useIntl } from "react-intl";

import {
    getSSRAutoScript,
    getThemeHtmlAttrs,
    getThemeStyles,
    setGlobalTheme
} from "@atlaskit/tokens";
import {
    AvailableProductsResponse,
    MeResponse
} from "@atlassiansox/asc-stargate";

import type {
    Template,
    TemplateCollection
} from "@/content/templates/template.types";
import { GetSignupUserTraitsResponse } from "@/libraries/bxp-web-stargate-client/src";

import { TemplateCategory } from "./components/confluence/templates/template-categories-search";
import { AppPricingData } from "./contentful";
import { Layout, PageProps } from "./layout";
import { getTemplateMetadata } from "./utils/confluence-templates-queries/get-template-metadata";
import { UserLocale } from "./utils/useGeoCurrency";
import { parseRoute } from "./wac-routes";

type PageWrapperProps = {
    location: Location;
    inlineCss?: string;
    pricing?: AppPricingData;
    availableProducts?: AvailableProductsResponse;
    signupUserTraits?: GetSignupUserTraitsResponse;
    userProfile?: MeResponse;
    countryCode?: UserLocale;
    pureClientMode?: boolean;
    templates?: Template[];
    partnerTemplates?: Template[];
    collections?: TemplateCollection[];
    categories?: TemplateCategory[];
    themeStyles?: Awaited<ReturnType<typeof getThemeStyles>>;
    children: React.ComponentType<PageProps>;
};

const SUPPORTED_LOCALES = [
    { hrefLang: "ja", langRoute: "ja" },
    { hrefLang: "fr", langRoute: "fr" },
    { hrefLang: "de", langRoute: "de" },
    { hrefLang: "es", langRoute: "es" },
    { hrefLang: "pt-BR", langRoute: "br" },
    { hrefLang: "zh-Hans", langRoute: "zh" },
    { hrefLang: "ko", langRoute: "ko" },
    { hrefLang: "ru", langRoute: "ru" },
    { hrefLang: "pl", langRoute: "pl" },
    { hrefLang: "it", langRoute: "it" },
    { hrefLang: "nl", langRoute: "nl" },
    { hrefLang: "hu", langRoute: "hu" }
];

const UNSUPPORTED_SEARCH_ENGINE_ROUTES = [
    "/ondemand/signup/checkemail",
    "/ondemand/signup/confirmation"
];

// eslint-disable-next-line complexity
export const PageWrapper = (props: PageWrapperProps) => {
    const {
        location,
        inlineCss,
        pricing,
        availableProducts,
        signupUserTraits,
        userProfile,
        countryCode,
        pureClientMode,
        templates,
        partnerTemplates,
        collections,
        categories,
        themeStyles
    } = props;
    const intl = useIntl();

    const baseUrl = "https://www.atlassian.com";
    const { locale, path, variant } = parseRoute(location);
    const route = path !== "" ? "/" + path : "";

    const localePrefix = locale === "en" ? "" : `/${locale}`;

    const themePrefs = { colorMode: "light" } as const;
    const themeAttrs = getThemeHtmlAttrs(themePrefs);
    const ssrAutoScript = getSSRAutoScript(themePrefs.colorMode);

    useEffect(() => {
        // Theme config for client-only rendering
        setGlobalTheme(themePrefs);
    }, [themePrefs]);

    let title, description;
    switch (route) {
        case "/software/jira":
            title = intl.formatMessage({
                id: "JiraSoftwarePage.title",
                defaultMessage:
                    "Jira | Issue & Project Tracking Software | Atlassian"
            });

            if (variant?.length) {
                description = intl.formatMessage({
                    id: "JiraSoftwarePage.paid.description",
                    defaultMessage:
                        "Plan, track, and manage your agile and software development projects in Jira. Customize your workflow, collaborate, and release great software."
                });
            } else {
                description = intl.formatMessage({
                    id: "JiraSoftwarePage.description",
                    defaultMessage:
                        "Make the impossible, possible in Jira. Plan, track, and release world-class software with the number one project management tool for agile teams."
                });
            }
            break;
        case "/software/jira/product-discovery":
            title = intl.formatMessage({
                id: "JiraProductDiscoveryPage.title",
                defaultMessage:
                    "Jira Product Discovery | prioritization and roadmapping tool"
            });
            description = intl.formatMessage({
                id: "JiraProductDiscoveryPage.description",
                defaultMessage:
                    "Organize and prioritize customer insights and product ideas, create custom and shareable roadmaps, and build products that make an impact - all in Jira."
            });
            break;
        case "/software/jira/pricing":
            title = intl.formatMessage({
                id: "JiraSoftwarePricingPage.title",
                defaultMessage:
                    " Jira Pricing - Monthly and Annual Subscription Cost per User"
            });
            description = intl.formatMessage({
                id: "JiraSoftwarePricingPage.description",
                defaultMessage:
                    "The #1 software development tool used by agile teams. Try it free or buy now. Available for cloud or server. Built for teams of all sizes."
            });
            break;
        case "/software/jira/service-management":
            title = intl.formatMessage({
                id: "JiraServiceManagementPage.title",
                defaultMessage:
                    "Jira Service Management | A new take on ITSM software"
            });

            if (
                !variant ||
                variant?.startsWith("anchor-lp-jsm") ||
                variant === "paid-lp-jsm-incidentchangemgmt" ||
                variant === "paid-lp-jsm-itsm"
            ) {
                description = intl.formatMessage({
                    id: "JiraServiceManagementPage.description",
                    defaultMessage:
                        "Make the impossible, possible with Jira Service Management. ITSM collaboration software that unlocks teams."
                });
            } else {
                description = intl.formatMessage({
                    id: "JiraServiceManagementPage.paid.description",
                    defaultMessage:
                        "Jira Service Desk is now Jira Service Management. Jira Service Management is ITSM software that unlocks teams."
                });
            }
            break;
        case "/software/jira/service-management/pricing":
            title = intl.formatMessage({
                id: "JiraServiceManagementSoftwarePricingPage.title",
                defaultMessage: "Pricing - Jira Service Management | Atlassian"
            });
            description = intl.formatMessage({
                id: "JiraServiceManagementSoftwarePricingPage.description",
                defaultMessage:
                    "Jira Service Management brings you a collaborative IT service desk with a powerful ticketing system, a self-service knowledge base and real-time reporting"
            });
            break;
        case "/software/confluence":
            title = intl.formatMessage({
                id: "ConfluencePage.title",
                defaultMessage:
                    "Confluence | Your Remote-Friendly Team Workspace | Atlassian"
            });

            if (variant?.length) {
                description = intl.formatMessage({
                    id: "ConfluencePage.paid.description",
                    defaultMessage:
                        "Confluence is where you create, organize and discuss work with your team."
                });
            } else {
                description = intl.formatMessage({
                    id: "ConfluencePage.description",
                    defaultMessage:
                        "Make the impossible, possible with Confluence Cloud. Your team workspace trusted for documentation, project collaboration, Jira integrations, and more!"
                });
            }
            break;
        case "/software/confluence/templates/search":
            title = intl.formatMessage({
                id: "ConfluenceTemplatesSearchPage.title",
                defaultMessage: "Search Results | Atlassian"
            });
            break;
        case "/software/confluence/pricing":
            title = intl.formatMessage({
                id: "ConfluencePricingPage.title",
                defaultMessage:
                    "Confluence Pricing: Free and Paid Plans | Atlassian"
            });
            description = intl.formatMessage({
                id: "ConfluencePricingPage.paid.description",
                defaultMessage:
                    "Compare pricing for Confluence across our Standard, Premium, and Enterprise plans or get started for free today."
            });

            break;
        case "/jira":
            title = intl.formatMessage({
                id: "JiraLandingPage.title",
                defaultMessage:
                    "Jira Products: Accomplish Great Work as a Team | Atlassian"
            });
            description = intl.formatMessage({
                id: "JiraLandingPage.description",
                defaultMessage:
                    "Jira products are a family of project planning & work management solutions that empower all teams to work with agility and do their best - together."
            });

            break;
        case "/ondemand/signup/confirmation":
            title = intl.formatMessage({
                id: "ConfirmationPage.title",
                defaultMessage: "Confirmation | Atlassian"
            });
            description = undefined;
            break;
        case "/ondemand/signup/checkemail":
            title = intl.formatMessage({
                id: "CheckemailPage.title",
                defaultMessage: "Check Email | Atlassian"
            });
            description = intl.formatMessage({
                id: "CheckemailPage.description",
                defaultMessage:
                    "Millions of users globally rely on Atlassian products every day for improving software development, project management, collaboration, and code quality."
            });
            break;
        case "/try/cloud/signup":
            title = intl.formatMessage({
                id: "SignupPage.title",
                defaultMessage: "Sign up - Try Atlassian Cloud | Atlassian"
            });
            description = intl.formatMessage({
                id: "SignupPage.description",
                defaultMessage: "Try our products in the Cloud, free for 7 days"
            });
            break;
        case "/software/jira/features":
            title = intl.formatMessage({
                id: "JiraSoftwareFeaturesPage.title",
                defaultMessage: "Jira Software - Features | Atlassian"
            });
            description = intl.formatMessage({
                id: "JiraSoftwareFeaturesPage.description",
                defaultMessage:
                    "Jira Software is equipped with features that support every stage of your software development process to help you plan, track and report."
            });
            break;
        case "/try":
            title = intl.formatMessage({
                id: "TryPage.title",
                defaultMessage: "Try Atlassian Products for Free | Atlassian"
            });
            description = intl.formatMessage({
                id: "TryPage.description",
                defaultMessage:
                    "Try Atlassian software development tools for free. See how Jira, Confluence, and our other tools can help your team track projects and collaborate"
            });
            break;
        case "/software/jira/free":
            title = intl.formatMessage({
                id: "JiraSoftwareFreePage.title",
                defaultMessage:
                    "Jira Free: Get Jira Software for Free | Atlassian"
            });
            description = intl.formatMessage({
                id: "JiraSoftwareFreePage.description",
                defaultMessage:
                    "Get Jira for free. The #1 software development tool used by agile teams is free for up to 10 users - get started today."
            });
            break;
        case "":
            title = intl.formatMessage({
                id: "HomePage.title",
                defaultMessage:
                    "Collaboration software for software, IT and business teams"
            });
            description = intl.formatMessage({
                id: "HomePage.description",
                defaultMessage:
                    "Atlassian's team collaboration software like Jira, Confluence and Trello help teams organize, discuss, and complete shared work."
            });
            break;
        case "/enterprise/contact":
            title = intl.formatMessage({
                id: "EnterpriseContactPage.title",
                defaultMessage:
                    "Contact Atlassian for Enterprise Solutions | Atlassian"
            });
            description = intl.formatMessage({
                id: "EnterpriseContactPage.description",
                defaultMessage:
                    "Contact us to learn more about our enterprise offerings and solutions. Speak to an Atlassian enterprise expert today."
            });
            break;
        case "/enterprise":
            title = intl.formatMessage({
                id: "EnterprisePage.title",
                defaultMessage: "Enterprise | Atlassian"
            });
            description = intl.formatMessage({
                id: "EnterprisePage.description",
                defaultMessage:
                    "Power your organization with our enterprise suite of products to promote team agility, supercharge IT capabilities, and enable teams across your enterprise"
            });
            break;
        default:
            title = intl.formatMessage({
                id: "404.title",
                defaultMessage: " | Atlassian"
            });
            if (route.startsWith("/software/confluence/templates")) {
                title = intl.formatMessage({
                    id: "ConfluenceTemplatesPage.title",
                    defaultMessage: "Confluence Templates | Atlassian"
                });
                description = intl.formatMessage({
                    id: "ConfluenceTemplatesPage.description",
                    defaultMessage:
                        "Confluence templates help teams create, organize, and discuss work. Get started with our template library."
                });
                // get the metadata from the template data
                const templatePageMeta = getTemplateMetadata({
                    route,
                    templates: props.templates,
                    collections: props.collections,
                    categories: props.categories
                });
                if (templatePageMeta?.title) {
                    title = templatePageMeta.title;
                    switch (templatePageMeta.titleSuffix) {
                        case "template":
                            title +=
                                " " +
                                intl.formatMessage({
                                    id: "ConfluenceTemplatesDetailPage.title-suffix",
                                    defaultMessage: "Template | Atlassian"
                                });
                            break;
                        case "collection":
                            title +=
                                " " +
                                intl.formatMessage({
                                    id: "ConfluenceTemplatesCollectionDetailPage.title-suffix",
                                    defaultMessage: "Collection | Atlassian"
                                });
                            break;
                        default:
                            title += " | Atlassian";
                    }
                }
                if (templatePageMeta?.description) {
                    description = templatePageMeta.description;
                }
            }
            break;
    }

    if (pureClientMode) {
        return (
            <>
                <style
                    suppressHydrationWarning
                    dangerouslySetInnerHTML={{ __html: inlineCss || "" }}
                />
                <link rel="canonical" href={`${baseUrl}${route}`} />
                <link
                    rel="alternate"
                    href={`${baseUrl}${route}`}
                    hrefLang="x-default"
                />
                {SUPPORTED_LOCALES.map(({ hrefLang, langRoute }) => (
                    <link
                        key={hrefLang}
                        rel="alternate"
                        href={`${baseUrl}/${langRoute}${route}`}
                        hrefLang={hrefLang}
                    />
                ))}
                <Layout
                    location={location}
                    pricing={pricing}
                    availableProducts={availableProducts}
                    signupUserTraits={signupUserTraits}
                    templates={templates}
                    partnerTemplates={partnerTemplates}
                    collections={collections}
                    categories={categories}
                    userProfile={userProfile}
                    countryCode={countryCode}
                    themeStyles={themeStyles}
                >
                    {props.children}
                </Layout>
            </>
        );
    }

    return (
        <html lang={intl.locale} {...themeAttrs}>
            <head>
                <meta charSet="utf-8" />
                <title>{title}</title>
                {description && (
                    <meta name="description" content={description} />
                )}
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1"
                />
                <meta httpEquiv="X-UA-Compatible" content="IE=edge, chrome=1" />
                <meta name="author" content="Atlassian" />
                {UNSUPPORTED_SEARCH_ENGINE_ROUTES.includes(route) && (
                    <meta name="robots" content="noindex,nofollow" />
                )}

                <style
                    suppressHydrationWarning
                    dangerouslySetInnerHTML={{ __html: inlineCss || "" }}
                    id="ssr_style"
                />
                <link
                    rel="canonical"
                    href={`${baseUrl}${localePrefix}${route}`}
                />
                <link
                    rel="alternate"
                    href={`${baseUrl}${route}`}
                    hrefLang="x-default"
                />
                {SUPPORTED_LOCALES.map(({ hrefLang, langRoute }) => (
                    <link
                        key={hrefLang}
                        rel="alternate"
                        href={`${baseUrl}/${langRoute}${route}`}
                        hrefLang={hrefLang}
                    />
                ))}
                {(themeStyles || []).map((theme) => (
                    <link
                        rel="stylesheet"
                        key={theme.id}
                        {...theme.attrs}
                        href={`${
                            process.env["ASSET_PREFIX"] || ""
                        }/assets/atlaskit-tokens_${theme.id}.css`}
                    />
                ))}
                {ssrAutoScript ? (
                    <script
                        dangerouslySetInnerHTML={{ __html: ssrAutoScript }}
                    />
                ) : null}
                <script
                    id="onetrust"
                    type="text/javascript"
                    src={process.env["ONETRUST_SCRIPT_URL"]}
                    data-domain={process.env["ONETRUST_SCRIPT_GUID"]}
                    data-test={process.env["ONETRUST_DATA_TEST"]}
                    data-geofence="false"
                    data-sync="true"
                />
            </head>
            <body>
                <div id="wac-root">
                    <Layout
                        location={location}
                        pricing={pricing}
                        availableProducts={availableProducts}
                        signupUserTraits={signupUserTraits}
                        templates={templates}
                        partnerTemplates={partnerTemplates}
                        collections={collections}
                        categories={categories}
                        userProfile={userProfile}
                        countryCode={countryCode}
                        themeStyles={themeStyles}
                    >
                        {props.children}
                    </Layout>
                </div>
            </body>
        </html>
    );
};
